import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
    FaCheckCircle,
    FaEdit,
    FaFileAlt,
    FaFileUpload,
    FaPlusCircle,
    FaRedoAlt,
    FaTrashAlt,
} from "react-icons/fa";
import { IoCloudUploadOutline } from "react-icons/io5";

const ProfilePage = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("userId");

    const [points, setPoints] = useState([]);
    const [newPoint, setNewPoint] = useState("");
    const [editIndex, setEditIndex] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [profile, setProfile] = useState({
        hasResume: false,
        resumeFileName: "",
        resumeContentType: "",
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [resumeMode, setResumeMode] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!userId || !apiUrl) {
                setError("Missing user session or API URL.");
                setLoading(false);
                return;
            }

            try {
                const response = await axios.get(`${apiUrl}/profile/${userId}`);
                setPoints(response.data.points || []);
                setProfile({
                    hasResume: response.data.has_resume || false,
                    resumeFileName: response.data.resume_file_name || "",
                    resumeContentType: response.data.resume_content_type || "",
                });
                setResumeMode(response.data.has_resume ? null : "replace");
            } catch (err) {
                setError("Failed to load your profile.");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [apiUrl, userId]);

    const refreshProfile = async () => {
        const response = await axios.get(`${apiUrl}/profile/${userId}`);
        setPoints(response.data.points || []);
        setProfile({
            hasResume: response.data.has_resume || false,
            resumeFileName: response.data.resume_file_name || "",
            resumeContentType: response.data.resume_content_type || "",
        });
        setResumeMode(response.data.has_resume ? null : "replace");
    };

    const savePoints = async (updatedPoints) => {
        setSaving(true);
        setError("");
        try {
            const response = await axios.put(`${apiUrl}/profile/${userId}`, {
                points: updatedPoints,
            });
            setPoints(response.data.points || []);
            setProfile((current) => ({
                ...current,
                hasResume: response.data.has_resume ?? current.hasResume,
                resumeFileName:
                    response.data.resume_file_name ?? current.resumeFileName,
                resumeContentType:
                    response.data.resume_content_type ?? current.resumeContentType,
            }));
            setNotice("Profile data saved.");
        } catch (err) {
            setError("Failed to save profile data.");
        } finally {
            setSaving(false);
        }
    };

    const handleAddPoint = async () => {
        const value = newPoint.trim();
        if (!value) return;

        const updatedPoints = [...points, value];
        setNewPoint("");
        await savePoints(updatedPoints);
    };

    const handleDeletePoint = async (index) => {
        const updatedPoints = points.filter((_, i) => i !== index);
        await savePoints(updatedPoints);
    };

    const handleEditPoint = (index) => {
        setEditIndex(index);
        setEditValue(points[index]);
    };

    const handleSaveEdit = async () => {
        const updatedPoints = [...points];
        updatedPoints[editIndex] = editValue.trim();
        setEditIndex(null);
        setEditValue("");
        await savePoints(updatedPoints);
    };

    const handleResumeChoice = (mode) => {
        setNotice(
            mode === "existing"
                ? "Keeping the current resume active."
                : "Upload a new file to replace the current resume."
        );
        setResumeMode(mode);
        if (mode === "replace" && fileInputRef.current) {
            fileInputRef.current.focus();
        }
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files?.[0] || null);
        setNotice("");
    };

    const handleUploadResume = async () => {
        if (!selectedFile) {
            setError("Choose a resume file first.");
            return;
        }

        setSaving(true);
        setError("");
        setNotice("");

        try {
            const formData = new FormData();
            formData.append("resume", selectedFile);

            const response = await axios.post(
                `${apiUrl}/profile/${userId}/resume`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setProfile({
                hasResume: true,
                resumeFileName:
                    response.data.profile?.resume_file_name || selectedFile.name,
                resumeContentType:
                    response.data.profile?.resume_content_type ||
                    selectedFile.type,
            });
            setResumeMode("existing");
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setNotice("Resume uploaded and replaced as the active version.");
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    "Failed to upload the resume."
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteResume = async () => {
        setSaving(true);
        setError("");
        setNotice("");

        try {
            const response = await axios.delete(
                `${apiUrl}/profile/${userId}/resume`
            );

            setProfile({
                hasResume: response.data.profile?.has_resume || false,
                resumeFileName: response.data.profile?.resume_file_name || "",
                resumeContentType:
                    response.data.profile?.resume_content_type || "",
            });
            setResumeMode("replace");
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setNotice("Resume removed from your profile.");
        } catch (err) {
            setError("Failed to remove the resume.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-sky-50">
                <p className="text-gray-700">Loading your profile...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 text-slate-900 p-6">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-4xl font-bold tracking-tight">
                        Your Profile
                    </h1>
                    <p className="mt-3 max-w-2xl text-slate-600">
                        Save non-sensitive profile notes and keep one active resume
                        on file for job applications.
                    </p>
                </motion.div>

                {(error || notice) && (
                    <div className="mb-6 space-y-2">
                        {error && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                                {error}
                            </div>
                        )}
                        {notice && (
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                                {notice}
                            </div>
                        )}
                    </div>
                )}

                <div className="grid gap-6 lg:grid-cols-2">
                    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="mb-5 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">
                                    Memory Notes
                                </h2>
                                <p className="text-sm text-slate-500">
                                    These are the non-sensitive details the agent
                                    can reuse with your confirmation.
                                </p>
                            </div>
                            <FaFileAlt className="text-2xl text-sky-500" />
                        </div>

                        <div className="mb-5 flex gap-3">
                            <label htmlFor="new-point-input" className="sr-only">
                                Add a new point
                            </label>
                            <input
                                id="new-point-input"
                                type="text"
                                className="flex-1 rounded-xl border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                placeholder="Add a new memory note"
                                value={newPoint}
                                onChange={(e) => setNewPoint(e.target.value)}
                            />
                            <button
                                onClick={handleAddPoint}
                                disabled={saving}
                                className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-3 font-medium text-white transition hover:bg-sky-700 disabled:opacity-50"
                            >
                                <FaPlusCircle />
                                Add
                            </button>
                        </div>

                        <div className="space-y-3">
                            {points.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">
                                    No memory notes saved yet.
                                </div>
                            ) : (
                                points.map((point, index) => (
                                    <div
                                        key={`${point}-${index}`}
                                        className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4"
                                    >
                                        {editIndex === index ? (
                                            <input
                                                id={`edit-point-input-${index}`}
                                                type="text"
                                                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500"
                                                value={editValue}
                                                onChange={(e) =>
                                                    setEditValue(e.target.value)
                                                }
                                            />
                                        ) : (
                                            <span className="flex-1 text-slate-800">
                                                {point}
                                            </span>
                                        )}

                                        <div className="flex shrink-0 items-center gap-2">
                                            {editIndex === index ? (
                                                <button
                                                    onClick={handleSaveEdit}
                                                    disabled={saving}
                                                    className="rounded-xl bg-emerald-600 px-3 py-2 text-white transition hover:bg-emerald-700 disabled:opacity-50"
                                                >
                                                    Save
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        handleEditPoint(index)
                                                    }
                                                    className="rounded-xl bg-slate-200 p-2 text-slate-700 transition hover:bg-slate-300"
                                                    aria-label={`Edit point: ${point}`}
                                                >
                                                    <FaEdit />
                                                </button>
                                            )}
                                            <button
                                                onClick={() =>
                                                    handleDeletePoint(index)
                                                }
                                                disabled={saving}
                                                className="rounded-xl bg-rose-100 p-2 text-rose-700 transition hover:bg-rose-200 disabled:opacity-50"
                                                aria-label={`Delete point: ${point}`}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="mb-5 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">
                                    Resume Vault
                                </h2>
                                <p className="text-sm text-slate-500">
                                    Only one active resume is kept per user.
                                    Uploading a new file replaces the current one.
                                </p>
                            </div>
                            <IoCloudUploadOutline className="text-3xl text-sky-500" />
                        </div>

                        {profile.hasResume ? (
                            <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-emerald-600" />
                                    <div>
                                        <p className="font-medium text-emerald-900">
                                            Active resume on file
                                        </p>
                                        <p className="text-sm text-emerald-800">
                                            {profile.resumeFileName}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                                No resume is saved yet.
                            </div>
                        )}

                        {profile.hasResume && resumeMode === null && (
                            <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <p className="mb-3 font-medium">
                                    A resume already exists. Would you like to
                                    keep it or replace it?
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={() => handleResumeChoice("existing")}
                                        className="rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white transition hover:bg-emerald-700"
                                    >
                                        Use existing resume
                                    </button>
                                    <button
                                        onClick={() => handleResumeChoice("replace")}
                                        className="rounded-xl bg-slate-200 px-4 py-2 font-medium text-slate-800 transition hover:bg-slate-300"
                                    >
                                        Upload a new one
                                    </button>
                                </div>
                            </div>
                        )}

                        {(!profile.hasResume || resumeMode === "replace") && (
                            <div className="space-y-4">
                                {profile.hasResume && (
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                                        Uploading a new file will replace the
                                        current resume. The backend keeps only one
                                        active resume per user.
                                    </div>
                                )}

                                {profile.hasResume && (
                                    <button
                                        onClick={() => handleResumeChoice("existing")}
                                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 font-medium text-white transition hover:bg-emerald-700"
                                    >
                                        <FaCheckCircle />
                                        Keep current resume instead
                                    </button>
                                )}

                                <div className="rounded-2xl border border-dashed border-slate-300 p-5">
                                    <label
                                        htmlFor="resume-file-input"
                                        className="mb-3 flex items-center gap-2 font-medium text-slate-700"
                                    >
                                        <FaFileUpload className="text-sky-600" />
                                        Choose a resume file
                                    </label>
                                    <input
                                        id="resume-file-input"
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx,.txt"
                                        onChange={handleFileChange}
                                        className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-xl file:border-0 file:bg-sky-600 file:px-4 file:py-2 file:font-medium file:text-white hover:file:bg-sky-700"
                                    />
                                    {selectedFile && (
                                        <p className="mt-3 text-sm text-slate-600">
                                            Selected file: {selectedFile.name}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={handleUploadResume}
                                        disabled={saving}
                                        className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-3 font-medium text-white transition hover:bg-sky-700 disabled:opacity-50"
                                    >
                                        <FaFileUpload />
                                        {profile.hasResume
                                            ? "Replace resume"
                                            : "Upload resume"}
                                    </button>

                                    {profile.hasResume && (
                                        <button
                                            onClick={handleDeleteResume}
                                            disabled={saving}
                                            className="inline-flex items-center gap-2 rounded-xl bg-rose-100 px-4 py-3 font-medium text-rose-700 transition hover:bg-rose-200 disabled:opacity-50"
                                        >
                                            <FaTrashAlt />
                                            Remove current resume
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {profile.hasResume && resumeMode === "existing" && (
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="flex items-center gap-3">
                                    <FaCheckCircle className="text-emerald-600" />
                                    <div>
                                        <p className="font-medium text-slate-800">
                                            Current resume will be used
                                        </p>
                                        <p className="text-sm text-slate-600">
                                            You can upload a replacement at any time.
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <button
                                        onClick={() => handleResumeChoice("replace")}
                                        className="inline-flex items-center gap-2 rounded-xl bg-slate-200 px-4 py-2 font-medium text-slate-800 transition hover:bg-slate-300"
                                    >
                                        <FaRedoAlt />
                                        Replace with a new file
                                    </button>
                                    <button
                                        onClick={refreshProfile}
                                        className="inline-flex items-center gap-2 rounded-xl bg-sky-100 px-4 py-2 font-medium text-sky-700 transition hover:bg-sky-200"
                                    >
                                        <FaRedoAlt />
                                        Refresh status
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
